import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Panel } from '@/components/Bicester';
import { fetchSubscribeSurplus } from '@/services/subscribe';
import { fetchBasicNum, fetchSubscribe } from '@/services/subscribe/make';
import { FormikProvider, useFormik, FieldArray } from 'formik';
import { defaultMakeInfo, makeSchema } from './constants';
import { Form } from '@/components/formik-rsuite';
import { usePersistFn, useRequest } from 'ahooks';
import { isBefore } from 'date-fns';
import _ from 'lodash';
import { SERVICE_STATUS } from '@/services/constants';
import { FULL_DATE } from '@/constants/dateFormat';
import dayjs from 'dayjs';
import { SUBSCRIBE_OPTION } from '../constants';
import { LEVEL_TYPE, VILLAGE_TYPE } from '@/constants';
import { Alert } from 'rsuite';
import PageLoader from '@/components/shared/PageLoader';
import { useReadyInfo } from '@/providers/ReadyProvider';

const Make = ({ member }) => {
  const [defaultInfo, setDefaultInfo] = useState(defaultMakeInfo);
  const { profile } = useReadyInfo();

  const formik = useFormik({
    initialValues: defaultInfo,
    validationSchema: makeSchema,
    enableReinitialize: true,
    async onSubmit(values) {
      const { option, ...rest } = values;
      const currentOpt = _.filter(option, 'check').map((item) =>
        _.pick(item, ['option_id', 'number', 'content'])
      );
      if (!currentOpt.length) {
        return formik.setFieldError('option.0.check', '请至少选择一项');
      }
      const { status } = await submitSubscribe({
        ...rest,
        member_id: member?.id,
        option: JSON.stringify(currentOpt),
      });
      if (status === SERVICE_STATUS.SUCCESS) {
        member?.id && member?.level === LEVEL_TYPE.YIZUN && getBasicNum(member?.id);
        Alert.success('预约成功');
        formik.resetForm();
      }
    },
  });

  const { run: submitSubscribe, loading: submitSubscribeLoading } = useRequest(fetchSubscribe, {
    manual: true,
  });

  const {
    run: getBasicNum,
    data: weekendSurplusNumbers = {},
    loading: numberLoading,
  } = useRequest<any>(fetchBasicNum, { manual: true });

  const { run: getSubscribeSurplus, loading: optionLoading } = useRequest<any>(
    fetchSubscribeSurplus,
    {
      manual: true,
      onSuccess({ option }, [date]) {
        const optionData =
          option?.map(({ option_id, option_name, time_data, content }) => ({
            option_id,
            option_name,
            number: '',
            content: '',
            timeData:
              time_data?.map(({ number, time_slot }) => ({
                label: time_slot,
                value: number,
              })) || [],
            contentPickerData: new Array(Number(content) || 0).fill(null).map((value, index) => {
              return {
                label: `${index + 1}人`,
                value: `${index + 1}`,
              };
            }),
            check: false,
          })) || [];
        formik.setFieldValue('option', optionData);
        date === defaultInfo.date &&
          !defaultInfo.option.length &&
          setDefaultInfo({ ...defaultInfo, option: optionData });
      },
    }
  );

  useEffect(() => {
    member?.id && member?.level === LEVEL_TYPE.YIZUN && getBasicNum(member?.id);
  }, [member?.id]);

  useEffect(() => {
    getSubscribeSurplus(formik?.values?.date);
  }, [formik?.values?.date]);

  const handleDateChange = usePersistFn((v) => {
    const date = dayjs(v).format(FULL_DATE);
    formik.resetForm();
    formik.setFieldValue('date', date);
  });

  const renderBasicInfo = useMemo(() => {
    return (
      <div className="mt-[80px] w-[190px] text-[#E16F2C] leading-7 ml-[20px]">
        <p>* 客户本年度服务权益余量</p>
        <ul>
          <li className="flex justify-between">
            <span>代客泊车</span>{' '}
            <span>{weekendSurplusNumbers?.subscribes_option_valet_count_surplus ?? 0}次</span>
          </li>
          <li className="flex justify-between">
            <span>贵宾休息室</span>{' '}
            <span>{weekendSurplusNumbers?.subscribes_option_VIP_Lounge_count_surplus ?? 0}次</span>
          </li>
        </ul>
        <p className="mt-[5px]">* 客户本年度已使用车牌</p>
        <ul>
          {weekendSurplusNumbers?.valet?.map((item) => (
            <li>{item?.content}</li>
          ))}
        </ul>
        <p className="mt-[5px]">* 客户本年度No show记录</p>
        <ul>
          {weekendSurplusNumbers?.no_show?.map((time) => (
            <li>{time} no show</li>
          ))}
        </ul>
      </div>
    );
  }, [weekendSurplusNumbers]);

  const loading = numberLoading || optionLoading;

  return (
    <div>
      {loading && <PageLoader />}
      <Panel
        leftShadow
        loading={submitSubscribeLoading}
        title="代客预约服务"
        style={{
          minHeight:
            'calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 195px)',
        }}
      >
        <FormikProvider value={formik}>
          <Form>
            <Card smDivider>
              <div className="flex replace-make-forms">
                <div className="flex-1">
                  <Card.DatePicker
                    disabledDate={(date) =>
                      isBefore(date, new Date(weekendSurplusNumbers.start_date))
                    }
                    formKey="date"
                    onChange={handleDateChange}
                    label="预约日期"
                    cleanable={false}
                  />
                  {member?.level === LEVEL_TYPE.YIZUN && renderBasicInfo}
                </div>
                <div className="flex-[4] relative">
                  <FieldArray name="option">
                    {() =>
                      formik?.values?.option?.map(
                        (
                          { option_id, option_name, timeData, contentPickerData, content, check },
                          i
                        ) => {
                          return (
                            <div key={option_id} className="flex">
                              <div className="w-[33.3%]">
                                <Card.Checkbox
                                  formKey={`option.${i}.check`}
                                  label={option_name}
                                  errorPlacement="bottomEnd"
                                  onCheck={(key, checked) => {
                                    if (!checked) {
                                      formik.setFieldValue(`option.${i}.number`, '');
                                      formik.setFieldValue(`option.${i}.content`, '');
                                    }
                                  }}
                                  contentWeight={3}
                                />
                              </div>
                              {[
                                SUBSCRIBE_OPTION.SA,
                                SUBSCRIBE_OPTION.LOUNGE,
                                SUBSCRIBE_OPTION.PARKING,
                              ].includes(option_id) && (
                                <div className="flex-[2] flex">
                                  <Card.Picker
                                    formKey={`option.${i}.number`}
                                    label={
                                      option_id === SUBSCRIBE_OPTION.LOUNGE
                                        ? '预约时间段'
                                        : '预约时间'
                                    }
                                    pickerData={timeData}
                                    disabled={!check}
                                    cleanable={false}
                                  />
                                  {option_id === SUBSCRIBE_OPTION.PARKING ? (
                                    <Card.Input
                                      formKey={`option.${i}.content`}
                                      label="预约车牌号"
                                      disabled={!check}
                                    />
                                  ) : (
                                    <Card.Picker
                                      formKey={`option.${i}.content`}
                                      label="预约人数"
                                      pickerData={contentPickerData}
                                      disabled={!check}
                                      cleanable={false}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }
                      )
                    }
                  </FieldArray>
                  <div
                    className="replace-make-forms-remark absolute bottom-0 right-0"
                    style={{ width: 'calc(66.7% * 13 / 16 + 16px * 5 + 26px)' }}
                  >
                    <Card.Input
                      inputType="textarea"
                      formKey="remark"
                      placeholder="请在此输入客户备注"
                    />
                  </div>
                </div>
              </div>
            </Card>
            <div className="flex justify-end mt-[50px]">
              <Button
                type="submit"
                className="w-[238px] h-[33px] text-[14px]"
                disabled={!member?.id}
              >
                提交代客预约
              </Button>
            </div>
          </Form>
        </FormikProvider>
      </Panel>
    </div>
  );
};

export default Make;
